import { commonUtils } from '@/helpers/commonUtils'
import PdfPage, { PageHeader, useMarkdownFontSize } from '../PdfPage/PdfPage'
import styles from './PdfPageMomentResults.module.less'
import { Divider } from 'antd'
import MarkdownRenderer from '@/components/MarkdownRenderer/MarkdownRenderer'
import ChartRangeView from '@/pages/ProjectDetailPage/components/StoryboardAnalysis/components/ChartRangeView/ChartRangeView'
import LegendDot from '@/pages/ProjectDetailPage/components/VideoAnalysis/VideoAnalysisChart/components/LegendDot/LegendDot'
import { useEffect, useRef, useState } from 'react'
import { genRangeDesc } from '@/pages/ProjectDetailPage/components/StoryboardAnalysis/StoryboardAnalysis'
import { useTranslation } from 'react-i18next'
import { isPortraitByProjectInfo } from '@/helpers/productCategory'

type MomentKey = keyof ProjectModels.ProjectDetail['dimensionMetrics']

interface Props {
  project: ProjectModels.ProjectDetail
  momentKey: MomentKey
  onRendered: (cnt: number) => void
  no: number
}

const getRangeDesc = (key: MomentKey, project: ProjectModels.ProjectDetail) => {
  if (key === 'firstThree') {
    const startTime = project.dimensionMetrics.firstThree.startTime
    const endTime = project.dimensionMetrics.firstThree.endTime
    return genRangeDesc({ startTime, endTime })
  }
  if (key === 'lastThree') {
    const startTime = project.dimensionMetrics.lastThree.startTime
    const endTime = project.dimensionMetrics.lastThree.endTime
    return genRangeDesc({ startTime, endTime })
  }

  const dimensionMetrics = project.dimensionMetrics[key]
  if (dimensionMetrics.periods) {
    return dimensionMetrics.periods
      .map((range) => {
        return genRangeDesc({ startTime: range.startTime, endTime: range.endTime })
      })
      .join('、')
  }
  return ''
}

const genData = (key: MomentKey, project: ProjectModels.ProjectDetail) => {
  if (key === 'firstThree') {
    const firstThreeDimension = project.dimensionMetrics.firstThree
    return {
      score: firstThreeDimension.score,
      meanScore: firstThreeDimension.meanScore,
      results: firstThreeDimension.reason.performance,
      ranges: [
        {
          start: firstThreeDimension.startTime,
          end: firstThreeDimension.endTime,
        },
      ],
    }
  }
  if (key === 'lastThree') {
    const lastThreeDimension = project.dimensionMetrics.lastThree
    return {
      score: lastThreeDimension.score,
      meanScore: lastThreeDimension.meanScore,
      results: lastThreeDimension.reason.performance,
      ranges: [
        {
          start: lastThreeDimension.startTime,
          end: lastThreeDimension.endTime,
        },
      ],
    }
  }
  const dimensionMetrics = project.dimensionMetrics[key]
  return {
    score: dimensionMetrics.overall.score,
    meanScore: dimensionMetrics.overall.meanScore,
    results: dimensionMetrics.overall.reason.performance,
    ranges: dimensionMetrics.periods
      .sort((a, b) => a.order - b.order)
      .map((item) => ({
        start: item.startTime,
        end: item.endTime,
      })),
  }
}

function PdfPageMomentResults(props: Props) {
  const BOX_MAX_HEIGHT = 600
  const resultsRef = useRef<HTMLDivElement>(null)
  const { fontsize, end } = useMarkdownFontSize(resultsRef, BOX_MAX_HEIGHT)
  const { t } = useTranslation()
  const titleMap: {
    [key in MomentKey]: string
  } = {
    firstThree: t('dashboard.projectDetailPage.storyboardView.theGolden3Seconds'),
    keyInformation: t('dashboard.projectDetailPage.storyboardView.informationTransmission'),
    brandConnection: t('dashboard.projectDetailPage.storyboardView.brandRelevance'),
    interestingEnagement: t('dashboard.projectDetailPage.storyboardView.interestStimulation'),
    lastThree: t('dashboard.projectDetailPage.storyboardView.theEnding3Seconds'),
  }
  const rangeDesc = getRangeDesc(props.momentKey, props.project)
  const dimensionMetrics = genData(props.momentKey, props.project)
  const isPortraitLayout = isPortraitByProjectInfo(props.project)
  const adDuration = Number(props.project.adDuration || 0)
  const detailSeconds = props.project.detailMetrics.length
  const isPortraitRegularCreative =
    isPortraitLayout &&
    (props.project.durationCategory === 'ten_plus' || adDuration > 10 || detailSeconds > 10)
  const isPortraitSplashCreative = isPortraitLayout && !isPortraitRegularCreative
  const [chartRendered, setChartRendered] = useState(false)

  useEffect(() => {
    if (chartRendered && end) {
      // 这里需要延迟一下，因为 chart 相关渲染也需要时间
      setTimeout(() => {
        props.onRendered(1)
      })
    }
  }, [chartRendered, end])

  return (
    <PdfPage bgSrc={commonUtils.getImagePath('PDFDownload/pdf-page-7/PDF-bg.jpg')} no={props.no}>
      <div className={styles.pdfPageMomentResults}>
        <PageHeader
          project={props.project}
          text={'03.' + t('dashboard.projectDetailPage.detailAnalysis')}
        />
        <div className={styles.titleContent}>
          <div className={styles.left}>
            <div className={styles.title}>{titleMap[props.momentKey]}</div>
            <Divider
              type='vertical'
              style={{ height: 32, marginLeft: 24, marginRight: 24, position: 'relative', top: 8 }}
            />
            <div className={styles.rangeDesc}>{rangeDesc}</div>
          </div>
          <div className={styles.right}>
            <span className={styles.score}>{dimensionMetrics.score}</span>
            <Divider type='vertical' style={{ height: 22, marginLeft: 20, marginRight: 20 }} />
            <span className={styles.meanScore}>
              {t('dashboard.projectDetailPage.storyboardView.norm')}: {dimensionMetrics.meanScore}
            </span>
          </div>
        </div>
        <div className={styles.resultContent}>
          <div className={styles.left}>
            <div className={styles.leftInner}>
              <div className={styles.title}>
                <img src={commonUtils.getImagePath('PDFDownload/robot-icon.png')} alt='' />
                <span>{t('dashboard.projectDetailPage.storyboardView.keyResult')}</span>
              </div>
              <div className={styles.results} ref={resultsRef}>
                <MarkdownRenderer
                  style={{ fontSize: fontsize }}
                  markdownText={dimensionMetrics.results}
                />
              </div>
            </div>
          </div>
          <div className={styles.right}>
            <div className={styles.rightInner}>
              <ChartRangeView
                className={styles.chartRange}
                chartClassName={
                  isPortraitSplashCreative ? `${styles.chart} ${styles.chartSplash}` : styles.chart
                }
                project={props.project}
                ranges={dimensionMetrics.ranges}
                options={{ height: '92%', labelVisible: true }}
                thumbnailMode={
                  isPortraitSplashCreative ? 'portrait-ppt' : isPortraitLayout ? 'portrait' : 'default'
                }
                onRender={() => {
                  setChartRendered(true)
                }}
              />
              <div className={styles.legendBox}>
                <LegendDot className={styles.legend} style={{ justifyContent: 'space-around' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </PdfPage>
  )
}

export default PdfPageMomentResults
